import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import SignalsSkeleton from "./SignalsSkeleton";
import { toasterState } from "Components/common/toaster";
import SignalsLibrary from "./SignalsLibrary";
import MySignals, { LiveFeed } from "./MySignals";
import { PAGE as FEED_PAGE } from "./SignalsFeed";
import SignalSetupDrawer from "./SignalSetupDrawer";
import SignalHowItWorks from "./SignalHowItWorks";
import SignalNotificationSettings from "./SignalNotificationSettings";
import SignalIcon from "./SignalIcon";
import CustomSignalDrawer from "./custom/CustomSignalDrawer";
import { isCustomRow } from "./custom/customSignalMeta";
import { AutomateHost, openAutomate } from "./SignalAutomateDrawer";
import "../agents/agents.scss";
import "./signals.scss";

const TABS = [["feed", "Live feed"], ["mine", "My signals"], ["library", "Signal templates"]];
// Links written before the tabs were renamed still land in the right place.
const ALIASES = { discover: "library", catalog: "library", templates: "library", active: "mine" };

/** Signals: the live feed, the signals you track, and the templates to track a new one from. Mounted at /signals. */
const SignalsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const asked = params.get("tab");
  const focusSignal = params.get("signal") || "";
  // An older "?tab=mine&signal=…" link meant the feed narrowed to that signal.
  const tab = ALIASES[asked] || (TABS.some(t => t[0] === asked) ? (asked === "mine" && focusSignal ? "feed" : asked) : "feed");
  // {definition, saved}: the Track form is open on this signal type (saved is null when adding).
  const [drawer, setDrawer] = useState(null);
  const [howItWorks, setHowItWorks] = useState(null);
  // {template, startText, saved}: the custom signal drawer. ?create=custom&text=… opens it with the ask filled in.
  const [custom, setCustom] = useState(() => (params.get("create") === "custom" ? { startText: params.get("text") || "" } : null));
  // A return visit draws straight from the store and refreshes underneath.
  // Each tab draws as soon as its own data is in: My signals on /saved, the Library on the catalog.
  const [savedReady, setSavedReady] = useState(() => signalsData.catalog.length > 0);
  const [catalogReady, setCatalogReady] = useState(() => signalsData.catalog.length > 0);
  const ready = tab === "library" ? catalogReady : savedReady;
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    signalsData.fetchCatalog().then(() => setCatalogReady(true));
    signalsData.fetchSaved().then(() => setSavedReady(true));
    signalsData.fetchCustomTemplates();
    // The feed doesn't need /saved, so it starts now; the feed component's own first load joins this request.
    if (tab === "feed") signalsData.fetchFeed({ view: "all", savedSignalId: focusSignal || null, page: 0, size: FEED_PAGE });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // My signals counts each signal's new moves from the feed, so a direct visit loads it too.
  useEffect(() => {
    if (tab === "mine") signalsData.fetchFeed({ view: "all", page: 0, size: FEED_PAGE, reuseWithinMs: 30 * 1000 });
  }, [tab]);

  const go = (t, signalId) => history.replace(`/signals?tab=${t}${signalId ? `&signal=${encodeURIComponent(signalId)}` : ""}`);
  const definitionOf = type => signalsData.catalog.find(d => d.id === type);
  const track = (definition, presetListId) => definition && setDrawer({ definition, saved: null, presetListId });
  const edit = row => (isCustomRow(row) ? setCustom({ saved: row }) : setDrawer({ definition: definitionOf(((row && row.signal) || {}).signalType), saved: row }));

  // ?track=<type>&list=<id> (e.g. "Track buying signals" after an agent run): the Track form opens on that list, once.
  useEffect(() => {
    const type = params.get("track");
    if (!catalogReady || !type || !definitionOf(type)) return;
    track(definitionOf(type), params.get("list") || undefined);
    go(tab);
  }, [catalogReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a save: to the feed, or (when the agent block was switched on) the agent drawer for this signal.
  const closeDrawer = (saved, savedView) => {
    setDrawer(null);
    if (savedView && savedView.signal) {
      openAutomate(savedView);
    } else if (saved) {
      const hiring = drawer && drawer.definition && drawer.definition.id === "hiring_intel" && !drawer.saved;
      toasterState.setToastMsg(hiring ? "Tracking started. Reading what your accounts are hiring for…" : "Tracking started. New moves show up in your feed.", "success");
      go("feed");
    }
  };

  // Saved: land on its own page, where the first check's matches show up.
  const closeCustom = savedView => {
    const editing = custom && custom.saved;
    setCustom(null);
    // The ?create=custom link has done its job; a reload must not reopen the drawer.
    if (params.get("create")) go(tab);
    const id = savedView && savedView.signal && savedView.signal.id;
    if (!id) return;
    toasterState.setToastMsg(editing ? "Saved. The next check uses your changes." : "Tracking started. The first check is running now.", "success");
    if (!editing) history.push(`/signals/tracked/${encodeURIComponent(id)}`);
  };

  return (
    <div className="sg-page">
      <div className="sg-head">
        <div>
          <h1>Signals</h1>
          <p>A live feed of what changed at the people and accounts you care about. Act on each one, or let an agent handle it.</p>
        </div>
        <div className="sg-head__right">
          {tab !== "library" && <button className="sg-btn" onClick={() => go("library")}><SignalIcon id="plus" size={16} /> Track a signal</button>}
        </div>
      </div>

      <div className="sg-tabs" role="tablist">
        {TABS.map(([value, label]) => (
          <button key={value} role="tab" aria-selected={tab === value} className={tab === value ? "on" : ""} onClick={() => go(value)}>
            {label}
            {value === "feed" && signalsData.unreadCount > 0 && <span className="sg-count sg-count--hot" title="New since you last looked">{signalsData.unreadCount} new</span>}
            {value === "mine" && signalsData.saved.length > 0 && <span className="sg-count">{signalsData.saved.length}</span>}
          </button>
        ))}
      </div>

      {!ready && <SignalsSkeleton />}
      {ready && tab === "library" && (
        <SignalsLibrary onTrack={track} onCreateCustom={template => setCustom({ template })} onHowItWorks={setHowItWorks} onOpenTracker={id => history.push(`/signals/tracked/${encodeURIComponent(id)}`)} />
      )}
      {ready && tab === "feed" && (
        <LiveFeed
          focusSignal={focusSignal}
          onFocus={id => go("feed", id)}
          onBrowse={() => go("library")}
          onTrack={track}
          onOpenSettings={() => setShowSettings(true)}
        />
      )}
      {ready && tab === "mine" && <MySignals onBrowse={() => go("library")} onEdit={edit} />}

      {showSettings && <SignalNotificationSettings onClose={() => setShowSettings(false)} />}
      {howItWorks && (
        <SignalHowItWorks
          definition={howItWorks}
          onClose={() => setHowItWorks(null)}
          onTrack={def => { setHowItWorks(null); track(def); }}
        />
      )}
      <AutomateHost />
      {custom && <CustomSignalDrawer template={custom.template} startText={custom.startText} saved={custom.saved} onClose={closeCustom} />}
      {drawer && drawer.definition && (
        <SignalSetupDrawer definition={drawer.definition} saved={drawer.saved} presetListId={drawer.presetListId} onClose={closeDrawer} />
      )}
    </div>
  );
};

export default observer(SignalsPage);
